/**
 * Ağırlıklı ve takvimli aday dağıtımı (v3.1 Dalga 2A dilim S9 — D3). Tasarım: docs/planning/v3/lead-dagitim-ve-kaynak-izleme.md
 * § 5; belge docs/spec/castinflow/lead-intake.md § 5.
 *
 * - Kurallar öncelik sırasıyla denenir; koşulu (mevcut liste/otomasyon koşul AST'si) tutan İLK kural uygulanır.
 * - Uygunluk: kullanıcı etkin, üye durdurulmamış, kuralın çalışma takvimi (ve üyenin kendi saatleri) şu an açık, günlük üst sınır
 *   ve kapasite dolmamış, kullanıcının kayıt okuma/yazma yetkisi var.
 * - Seçim (deterministik, uzun vadede tam oranlı): uygun üyeler arasında `atanan / ağırlık` en küçük → eşitlikte en eski atama →
 *   kullanıcı kimliği. Sayaçlar kural başına kilitlenir; seçim, sayaç, atama (ortak atama sözleşmesi, gerekçe `distribution`) ve
 *   karar kaydı TEK işlemdedir.
 * - Uygun temsilci yoksa aday BEKLEYEN DAĞITIM kuyruğuna düşer (sahipsiz; "Bugün" ekranının sahipsiz aday kovasında görünür);
 *   takvim açılınca arka plan taraması aynı algoritmayla dağıtır. Kural hatası ve "uygun kural yok" İSTİSNA kuyruğundadır.
 * - Her karar açıklanabilir: aday temsilcilerin anlık görüntüsü (ağırlık, sayaç, uygun mu, neden) saklanır.
 */
import { z } from 'zod';
import { filterAstSchema } from './list-query.js';
import { workIntervalSchema } from './work-calendar.js';
const uuid = z.guid();
export const DISTRIBUTION_WINDOWS = ['rule_lifetime', 'calendar_month'];
export const DISTRIBUTION_WINDOW_LABELS = {
    rule_lifetime: 'Kural boyunca (yeniden başlatılana dek)',
    calendar_month: 'Takvim ayı (her ay sıfırdan)',
};
export const DISTRIBUTION_LIMITS = {
    maxRules: 50,
    maxMembers: 50,
    weightMin: 1,
    weightMax: 100,
    dailyCapMax: 10_000,
    capacityMax: 100_000,
};
/** Uygun olmama gerekçeleri (kapalı küme) */
export const DISTRIBUTION_INELIGIBLE = [
    'inactive',
    'paused',
    'calendar_closed',
    'outside_hours',
    'daily_cap',
    'capacity',
    'no_access',
];
export const DISTRIBUTION_INELIGIBLE_LABELS = {
    inactive: 'Kullanıcı etkin değil',
    paused: 'Dağıtım durduruldu',
    calendar_closed: 'Çalışma takvimine göre mesai dışı',
    outside_hours: 'Temsilcinin çalışma saatleri dışında',
    daily_cap: 'Günlük üst sınır doldu',
    capacity: 'Kapasite dolu (açık aday sayısı)',
    no_access: 'Aday kayıtlarını okuma/yazma yetkisi yok',
};
export const DISTRIBUTION_OUTCOMES = [
    'assigned',
    'pending',
    'no_rule',
    'fallback',
    'error',
    'manual_take',
];
export const DISTRIBUTION_OUTCOME_LABELS = {
    assigned: 'Kurala göre atandı',
    pending: 'Uygun temsilci yok — bekleyen dağıtımda',
    no_rule: 'Uygun kural yok — istisnalarda',
    fallback: 'Uygun kural yok — kaynağın varsayılan sorumlusuna atandı',
    error: 'Kural hatası — istisnalarda',
    manual_take: 'Kuyruktan elle atandı',
};
export const DISTRIBUTION_ERROR_CODES = [
    'rule_owner_inactive',
    'rule_owner_forbidden',
    'condition_invalid',
    'assignment_rejected',
    'internal',
];
export const DISTRIBUTION_ERROR_LABELS = {
    rule_owner_inactive: 'Kuralın sorumlu yöneticisi etkin değil',
    rule_owner_forbidden: 'Kuralın sorumlu yöneticisinin yönetim yetkisi ya da kayda erişimi yok',
    condition_invalid: 'Kural koşulu değerlendirilemedi',
    assignment_rejected: 'Atama ortak atama sözleşmesince reddedildi',
    internal: 'Beklenmeyen hata',
};
export const DISTRIBUTION_QUEUE_KINDS = ['pending', 'exception'];
// ---------------------------------------------------------------------------------------------------------------------
// Kural tanımı
// ---------------------------------------------------------------------------------------------------------------------
export const distributionMemberInputSchema = z
    .object({
    userId: uuid,
    weight: z
        .number()
        .int()
        .min(DISTRIBUTION_LIMITS.weightMin, 'ağırlık 1–100 arasında olmalı')
        .max(DISTRIBUTION_LIMITS.weightMax, 'ağırlık 1–100 arasında olmalı'),
    paused: z.boolean().default(false),
    dailyCap: z.number().int().min(1).max(DISTRIBUTION_LIMITS.dailyCapMax).nullable().default(null),
    capacity: z.number().int().min(1).max(DISTRIBUTION_LIMITS.capacityMax).nullable().default(null),
    /** Temsilcinin kendi çalışma saatleri (çalışma alanı saat diliminde); boş = kuralın takvimi yeterli */
    schedule: z.array(workIntervalSchema).max(28).nullable().default(null),
})
    .strict();
export const distributionRuleInputSchema = z
    .object({
    name: z.string().trim().min(1, 'ad gerekli').max(120),
    priority: z.number().int().min(0).max(10_000),
    isActive: z.boolean().default(true),
    /** Koşul (aday listesi süzgeç AST'si); boş = her aday */
    condition: filterAstSchema.nullable().default(null),
    window: z.enum(DISTRIBUTION_WINDOWS).default('rule_lifetime'),
    useWorkCalendar: z.boolean().default(true),
    /** Kuralın sorumlu yöneticisi (kural onun yetkisiyle değerlendirilir); verilmezse kaydeden */
    ownerUserId: uuid.optional(),
    members: z
        .array(distributionMemberInputSchema)
        .min(1, 'en az bir temsilci gerekli')
        .max(DISTRIBUTION_LIMITS.maxMembers),
})
    .strict()
    .superRefine((r, ctx) => {
    const seen = new Set();
    for (const m of r.members) {
        const id = m.userId.toLowerCase();
        if (seen.has(id))
            ctx.addIssue({ code: 'custom', message: 'temsilci tekrar ediyor', path: ['members'] });
        seen.add(id);
    }
});
export const distributionMemberViewSchema = z.object({
    userId: uuid,
    name: z.string(),
    active: z.boolean(),
    weight: z.number().int(),
    paused: z.boolean(),
    dailyCap: z.number().int().nullable(),
    capacity: z.number().int().nullable(),
    schedule: z.array(workIntervalSchema).nullable(),
    /** Güncel penceredeki atama sayısı */
    assignedCount: z.number().int(),
    lastAssignedAt: z.string().nullable(),
});
export const distributionRuleViewSchema = z.object({
    id: uuid,
    name: z.string(),
    priority: z.number().int(),
    isActive: z.boolean(),
    version: z.number().int(),
    condition: filterAstSchema.nullable(),
    window: z.enum(DISTRIBUTION_WINDOWS),
    windowKey: z.string(),
    useWorkCalendar: z.boolean(),
    ownerUserId: uuid,
    ownerName: z.string().nullable(),
    members: z.array(distributionMemberViewSchema),
    /**
     * Kuralın sorumlu yöneticisi şu an kuralı değerlendiremez (etkin değil ya da yönetim yetkisi yok): kural etkinse her aday
     * istisnaya düşer (`rule_owner_inactive` / `rule_owner_forbidden`). Düzeltme: sorumluyu değiştirin ya da kuralı durdurun.
     */
    ownerProblem: z.enum(['inactive', 'forbidden']).nullable(),
    updatedAt: z.string(),
    /** Sürüm kontrolü (If-Match) */
    etag: z.string(),
});
export const distributionRuleListSchema = z.object({
    data: z.array(distributionRuleViewSchema),
    timeZone: z.string(),
});
export const resetDistributionCountersInputSchema = z
    .object({ confirm: z.literal(true), reason: z.string().trim().min(5).max(500) })
    .strict();
// ---------------------------------------------------------------------------------------------------------------------
// Kararlar ve kuyruk
// ---------------------------------------------------------------------------------------------------------------------
export const distributionCandidateSchema = z.object({
    userId: uuid,
    name: z.string().nullable(),
    weight: z.number().int(),
    assignedCount: z.number().int(),
    lastAssignedAt: z.string().nullable(),
    eligible: z.boolean(),
    reasons: z.array(z.enum(DISTRIBUTION_INELIGIBLE)),
});
export const distributionDecisionViewSchema = z.object({
    id: uuid,
    leadId: uuid,
    ruleId: uuid.nullable(),
    ruleName: z.string().nullable(),
    ruleVersion: z.number().int().nullable(),
    outcome: z.enum(DISTRIBUTION_OUTCOMES),
    errorCode: z.enum(DISTRIBUTION_ERROR_CODES).nullable(),
    candidates: z.array(distributionCandidateSchema),
    chosenUserId: uuid.nullable(),
    chosenName: z.string().nullable(),
    decidedAt: z.string(),
    decidedByName: z.string().nullable(),
});
export const leadDistributionSchema = z.object({
    queue: z.object({ kind: z.enum(DISTRIBUTION_QUEUE_KINDS), since: z.string() }).nullable(),
    decisions: z.array(distributionDecisionViewSchema),
});
export const distributionQueueItemSchema = z.object({
    leadId: uuid,
    title: z.string(),
    recordNo: z.string().nullable(),
    sourceKey: z.string().nullable(),
    kind: z.enum(DISTRIBUTION_QUEUE_KINDS),
    since: z.string(),
    outcome: z.enum(DISTRIBUTION_OUTCOMES).nullable(),
    errorCode: z.enum(DISTRIBUTION_ERROR_CODES).nullable(),
    ruleName: z.string().nullable(),
    /** Kararın kuralı (kural silinmez; "Kuralı düzelt" bağlantısı) */
    ruleId: uuid.nullable(),
    /** Şirket adı ve kişi adı ayrı (başlık yalnız kişi adı olunca adaylar ayırt edilemiyordu) */
    companyName: z.string(),
    personName: z.string().nullable(),
    /** Bekleme gerekçesi: karardaki aday temsilcilerin uygun olmama nedenleri (tekilleştirilmiş, kapalı küme) */
    reasons: z.array(z.enum(DISTRIBUTION_INELIGIBLE)),
    /** Karardaki aday temsilci sayısı (0 = kuralda değerlendirilecek temsilci yoktu) */
    candidateCount: z.number().int(),
});
export const distributionQueueSchema = z.object({
    data: z.array(distributionQueueItemSchema),
    counts: z.object({ pending: z.number().int(), exception: z.number().int() }),
});
/** Yönetici kuyruktaki adayı seçtiği kullanıcıya atar (elle atama) */
export const distributionAssignInputSchema = z.object({ ownerId: uuid }).strict();
export const distributionQueueQuerySchema = z
    .object({ kind: z.enum(DISTRIBUTION_QUEUE_KINDS).default('pending') })
    .strict();
/**
 * Uygun üyeler arasında `atanan / ağırlık` oranı en küçük olan; eşitlikte son ataması en eski (hiç atanmamış en önce); sonra
 * kullanıcı kimliği (küçük harf, sözlük sırası). Oran karşılaştırması tam sayılarla yapılır (kayan nokta yok). Uygun üye yoksa null.
 */
export function pickDistributionMember(candidates) {
    let best = null;
    for (const c of candidates) {
        if (!c.eligible || c.weight < 1)
            continue;
        if (best === null) {
            best = c;
            continue;
        }
        const lhs = c.assignedCount * best.weight;
        const rhs = best.assignedCount * c.weight;
        if (lhs !== rhs) {
            if (lhs < rhs)
                best = c;
            continue;
        }
        const a = c.lastAssignedAt ?? Number.NEGATIVE_INFINITY;
        const b = best.lastAssignedAt ?? Number.NEGATIVE_INFINITY;
        if (a !== b) {
            if (a < b)
                best = c;
            continue;
        }
        if (c.userId.toLowerCase() < best.userId.toLowerCase())
            best = c;
    }
    return best?.userId ?? null;
}
// ---------------------------------------------------------------------------------------------------------------------
// Temsilci çalışma saatleri (saat dilimi duvar saati; yaz saati `Intl` ile)
// ---------------------------------------------------------------------------------------------------------------------
const wallFmt = new Map();
function wallOf(tz, at) {
    let f = wallFmt.get(tz);
    if (f === undefined) {
        f = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hourCycle: 'h23',
            weekday: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
        wallFmt.set(tz, f);
    }
    let wd = '';
    let h = 0;
    let m = 0;
    for (const p of f.formatToParts(new Date(at))) {
        if (p.type === 'weekday')
            wd = p.value;
        else if (p.type === 'hour')
            h = Number(p.value);
        else if (p.type === 'minute')
            m = Number(p.value);
    }
    const weekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(wd) + 1;
    return { weekday, minutes: h * 60 + m };
}
/** Verilen an, haftalık aralıklardan birinin içinde mi (yerel duvar saati; bitiş hariç) */
export function withinWeeklySchedule(intervals, timeZone, at) {
    const w = wallOf(timeZone, at);
    return intervals.some((i) => {
        if (i.weekday !== w.weekday)
            return false;
        const [sh, sm] = i.start.split(':');
        const [eh, em] = i.end.split(':');
        const s = Number(sh) * 60 + Number(sm);
        const e = Number(eh) * 60 + Number(em);
        return w.minutes >= s && w.minutes < e;
    });
}
//# sourceMappingURL=distribution.js.map